import { useMemo, useState } from 'react';
import { IServiceRequest } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import {
  ProjectFilter,
  setIdsSelected,
  collectStagedServiceRequests,
  filterActualChanges,
  filterServiceRequests,
  filterUnchanged,
  indexServiceRequestsById,
  toggleIdInSelection,
} from 'src/pages/ServiceRequests/components/ServiceRequestVisibilityDialogue/CustomHooks/useServiceRequestVisibilityFormState/helpers';

export type StepId = 1 | 2 | 3;

export const WIZARD_STEP_PICK: StepId = 1;
export const WIZARD_STEP_REVIEW: StepId = 2;
export const WIZARD_STEP_CONFIRMATION: StepId = 3;

const DEFAULT_TARGET_IS_INTERNAL_ONLY: boolean = true;

export interface UseServiceRequestVisibilityFormStateArgs {
  serviceRequests: IServiceRequest[];
}

export interface UseServiceRequestVisibilityFormStateReturn {
  currentStep: StepId;
  setCurrentStep: (step: StepId) => void;

  projectFilter: ProjectFilter;
  searchQuery: string;
  setProjectFilter: (projectId: ProjectFilter) => void;
  setSearchQuery: (query: string) => void;

  selectedIds: Set<string>;
  toggleSelection: (id: string) => void;
  setSelectAll: (ids: string[], selected: boolean) => void;
  clearSelection: () => void;

  targetIsInternalOnly: boolean;
  setTargetIsInternalOnly: (isInternalOnly: boolean) => void;

  filteredServiceRequests: IServiceRequest[];
  stagedServiceRequests: IServiceRequest[];
  actualChanges: IServiceRequest[];
  unchangedServiceRequests: IServiceRequest[];
  willChangeIds: string[];
  willBecomeInternalCount: number;
  willBecomeExternalCount: number;

  canContinueFromStep1: boolean;
  canApplyFromStep2: boolean;

  stageSelectedAndContinue: () => void;
  goToStep: (step: StepId) => void;
  reset: () => void;
}

export type { ProjectFilter } from 'src/pages/ServiceRequests/components/ServiceRequestVisibilityDialogue/CustomHooks/useServiceRequestVisibilityFormState/helpers';

export const useServiceRequestVisibilityFormState = ({
  serviceRequests,
}: UseServiceRequestVisibilityFormStateArgs): UseServiceRequestVisibilityFormStateReturn => {
  const [currentStep, setCurrentStepState] = useState<StepId>(WIZARD_STEP_PICK);
  const [projectFilter, setProjectFilterState] = useState<ProjectFilter>('all');
  const [searchQuery, setSearchQueryState] = useState<string>('');
  const [selectedIds, setSelectedIdsState] = useState<Set<string>>(new Set());
  const [targetIsInternalOnly, setTargetIsInternalOnlyState] = useState<boolean>(
    DEFAULT_TARGET_IS_INTERNAL_ONLY
  );

  const setCurrentStep = (step: StepId): void => {
    setCurrentStepState(step);
  };

  const setProjectFilter = (projectId: ProjectFilter): void => {
    setProjectFilterState(projectId);
  };

  const setSearchQuery = (query: string): void => {
    setSearchQueryState(query);
  };

  const toggleSelection = (id: string): void => {
    setSelectedIdsState((previous: Set<string>) =>
      toggleIdInSelection(previous, id)
    );
  };

  const setSelectAll = (ids: string[], selected: boolean): void => {
    setSelectedIdsState((previous: Set<string>) =>
      setIdsSelected(previous, ids, selected)
    );
  };

  const clearSelection = (): void => {
    setSelectedIdsState(new Set());
  };

  const setTargetIsInternalOnly = (isInternalOnly: boolean): void => {
    setTargetIsInternalOnlyState(isInternalOnly);
  };

  const serviceRequestsById: Map<string, IServiceRequest> = useMemo(() => {
    return indexServiceRequestsById(serviceRequests);
  }, [serviceRequests]);

  const filteredServiceRequests: IServiceRequest[] = useMemo(() => {
    return filterServiceRequests(serviceRequests, projectFilter, searchQuery);
  }, [serviceRequests, projectFilter, searchQuery]);

  const stagedServiceRequests: IServiceRequest[] = useMemo(() => {
    return collectStagedServiceRequests(selectedIds, serviceRequestsById);
  }, [selectedIds, serviceRequestsById]);

  const actualChanges: IServiceRequest[] = useMemo(() => {
    return filterActualChanges(stagedServiceRequests, targetIsInternalOnly);
  }, [stagedServiceRequests, targetIsInternalOnly]);

  const unchangedServiceRequests: IServiceRequest[] = useMemo(() => {
    return filterUnchanged(stagedServiceRequests, targetIsInternalOnly);
  }, [stagedServiceRequests, targetIsInternalOnly]);

  const willChangeIds: string[] = useMemo(() => {
    return actualChanges.map((sr: IServiceRequest) => sr.id);
  }, [actualChanges]);

  const willBecomeInternalCount: number = targetIsInternalOnly
    ? actualChanges.length
    : 0;

  const willBecomeExternalCount: number = targetIsInternalOnly
    ? 0
    : actualChanges.length;

  const canContinueFromStep1: boolean = selectedIds.size > 0;
  const canApplyFromStep2: boolean = actualChanges.length > 0;

  const stageSelectedAndContinue = (): void => {
    if (!canContinueFromStep1) {
      return;
    }

    setCurrentStepState(WIZARD_STEP_REVIEW);
  };

  const goToStep = (step: StepId): void => {
    if (step === WIZARD_STEP_REVIEW && !canContinueFromStep1) {
      return;
    }

    if (step === WIZARD_STEP_CONFIRMATION && !canApplyFromStep2) {
      return;
    }

    setCurrentStepState(step);
  };

  const reset = (): void => {
    setCurrentStepState(WIZARD_STEP_PICK);
    setProjectFilterState('all');
    setSearchQueryState('');
    setSelectedIdsState(new Set());
    setTargetIsInternalOnlyState(DEFAULT_TARGET_IS_INTERNAL_ONLY);
  };

  return {
    currentStep,
    setCurrentStep,

    projectFilter,
    searchQuery,
    setProjectFilter,
    setSearchQuery,

    selectedIds,
    toggleSelection,
    setSelectAll,
    clearSelection,

    targetIsInternalOnly,
    setTargetIsInternalOnly,

    filteredServiceRequests,
    stagedServiceRequests,
    actualChanges,
    unchangedServiceRequests,
    willChangeIds,
    willBecomeInternalCount,
    willBecomeExternalCount,

    canContinueFromStep1,
    canApplyFromStep2,

    stageSelectedAndContinue,
    goToStep,
    reset,
  };
};